import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { listManualOperatingTargets } from '@/api/manualOperating'
import { listNodes } from '@/api/nodes'
import Pagination from '@/components/Pagination'
import type { ManualOperatingTarget, NodeSummary } from '@/types'
import { manualOperatingTargetPath } from '@/utils/navigation'
import './manualOperating.css'

const PAGE_SIZE = 12
const METADATA_LIMIT = 1000
const UNASSIGNED_COMPANY = 'Unassigned'
const NO_SITE = 'No site/area'

interface EnrichedTarget extends ManualOperatingTarget {
  company: string
  site: string
}

interface SiteGroup {
  site: string
  targets: EnrichedTarget[]
}

interface CompanyGroup {
  company: string
  sites: SiteGroup[]
}

function normalizedGroup(value: string | null | undefined, fallback: string): string {
  return value?.trim() || fallback
}

function compareTarget(left: EnrichedTarget, right: EnrichedTarget): number {
  return left.company.localeCompare(right.company)
    || left.site.localeCompare(right.site)
    || left.display_name.localeCompare(right.display_name)
    || left.target_id.localeCompare(right.target_id)
}

function groupPageTargets(targets: EnrichedTarget[]): CompanyGroup[] {
  const companies = new Map<string, Map<string, EnrichedTarget[]>>()

  for (const target of targets) {
    if (!companies.has(target.company)) companies.set(target.company, new Map())
    const sites = companies.get(target.company)!
    if (!sites.has(target.site)) sites.set(target.site, [])
    sites.get(target.site)!.push(target)
  }

  return Array.from(companies.entries()).map(([company, sites]) => ({
    company,
    sites: Array.from(sites.entries()).map(([site, siteTargets]) => ({
      site,
      targets: siteTargets,
    })),
  }))
}

export default function ManualOperatingListPage() {
  const [targets, setTargets] = useState<ManualOperatingTarget[]>([])
  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    let cancelled = false

    Promise.all([
      listManualOperatingTargets(),
      listNodes({ limit: METADATA_LIMIT }),
    ])
      .then(([nextTargets, nodeResponse]) => {
        if (cancelled) return
        setTargets(nextTargets)
        setNodes(nodeResponse.items)
        setError(null)
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        setError(
          (caught as { message?: string }).message
          ?? '대상 목록을 불러오지 못했습니다.',
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const companyFilter = searchParams.get('company') ?? ''
  const siteFilter = searchParams.get('site') ?? ''
  const page = useMemo(() => {
    const raw = Number(searchParams.get('page') ?? '1')
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1
  }, [searchParams])

  const enrichedTargets = useMemo(() => {
    const nodeMap = new Map(nodes.map((node) => [node.id, node]))
    return targets.map<EnrichedTarget>((target) => {
      const node = nodeMap.get(target.physical_node_id)
      return {
        ...target,
        company: normalizedGroup(node?.company, UNASSIGNED_COMPANY),
        site: normalizedGroup(node?.site, NO_SITE),
      }
    })
  }, [nodes, targets])

  const companies = useMemo(
    () => Array.from(new Set(enrichedTargets.map((target) => target.company))).sort(),
    [enrichedTargets],
  )
  const sites = useMemo(
    () => Array.from(new Set(
      enrichedTargets
        .filter((target) => !companyFilter || target.company === companyFilter)
        .map((target) => target.site),
    )).sort(),
    [companyFilter, enrichedTargets],
  )

  const filteredTargets = useMemo(
    () => enrichedTargets
      .filter((target) => {
        if (companyFilter && target.company !== companyFilter) return false
        if (siteFilter && target.site !== siteFilter) return false
        return true
      })
      .sort(compareTarget),
    [companyFilter, enrichedTargets, siteFilter],
  )

  const totalPages = Math.max(1, Math.ceil(filteredTargets.length / PAGE_SIZE))
  const effectivePage = Math.min(page, totalPages)
  const pageTargets = useMemo(
    () => filteredTargets.slice(
      (effectivePage - 1) * PAGE_SIZE,
      effectivePage * PAGE_SIZE,
    ),
    [effectivePage, filteredTargets],
  )
  const groupedTargets = useMemo(() => groupPageTargets(pageTargets), [pageTargets])

  const companyTotals = useMemo(() => {
    const totals = new Map<string, number>()
    for (const target of filteredTargets) {
      totals.set(target.company, (totals.get(target.company) ?? 0) + 1)
    }
    return totals
  }, [filteredTargets])

  const siteTotals = useMemo(() => {
    const totals = new Map<string, Map<string, number>>()
    for (const target of filteredTargets) {
      if (!totals.has(target.company)) totals.set(target.company, new Map())
      const companySites = totals.get(target.company)!
      companySites.set(target.site, (companySites.get(target.site) ?? 0) + 1)
    }
    return totals
  }, [filteredTargets])

  const setCompanyFilter = useCallback((company: string) => {
    const nextParams = new URLSearchParams(searchParams)
    if (company) nextParams.set('company', company)
    else nextParams.delete('company')
    nextParams.delete('site')
    nextParams.delete('page')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const setSiteFilter = useCallback((site: string) => {
    const nextParams = new URLSearchParams(searchParams)
    if (site) nextParams.set('site', site)
    else nextParams.delete('site')
    nextParams.delete('page')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const setPage = useCallback((nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams)
    if (nextPage <= 1) nextParams.delete('page')
    else nextParams.set('page', String(nextPage))
    setSearchParams(nextParams)
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages)
  }, [loading, page, setPage, totalPages])

  return (
    <div className="manual-operating-page">
      <h1 className="manual-heading">Manual Operating</h1>
      <p className="manual-subtitle">
        영상과 설정이 준비된 GuardianNode를 선택하세요. 명령은 camera가 아닌 논리 GuardianNode 단위로 전송됩니다.
      </p>
      {loading && <div className="manual-card">Loading...</div>}
      {error && <div className="manual-result error" role="alert">{error}</div>}
      {!loading && !error && (
        <>
          <div
            className="manual-target-filters"
            role="group"
            aria-label="Manual Operating target filters"
          >
            <label className="manual-target-filter">
              <span>Company</span>
              <select
                value={companyFilter}
                onChange={(event) => setCompanyFilter(event.target.value)}
              >
                <option value="">All companies</option>
                {companies.map((company) => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </label>
            <label className="manual-target-filter">
              <span>Site/area</span>
              <select
                value={siteFilter}
                onChange={(event) => setSiteFilter(event.target.value)}
              >
                <option value="">All sites/areas</option>
                {sites.map((site) => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
            </label>
          </div>

          {targets.length === 0 && (
            <div className="manual-card">Manual Operating 대상이 없습니다.</div>
          )}
          {targets.length > 0 && filteredTargets.length === 0 && (
            <div className="manual-card">선택한 고객사와 구역에 Manual Operating 대상이 없습니다.</div>
          )}
          {filteredTargets.length > 0 && (
            <div className="manual-company-groups">
              {groupedTargets.map((companyGroup) => {
                const companyShown = companyGroup.sites.reduce(
                  (count, siteGroup) => count + siteGroup.targets.length,
                  0,
                )
                return (
                  <section key={companyGroup.company} className="manual-company-section">
                    <div className="manual-group-heading">
                      <h2>{companyGroup.company}</h2>
                      <span>
                        {companyShown} shown / {companyTotals.get(companyGroup.company) ?? companyShown} targets
                      </span>
                    </div>
                    <div className="manual-site-groups">
                      {companyGroup.sites.map((siteGroup) => (
                        <section key={siteGroup.site} className="manual-site-section">
                          <div className="manual-site-heading">
                            <h3>{siteGroup.site}</h3>
                            <span>
                              {siteGroup.targets.length} shown /{' '}
                              {siteTotals.get(companyGroup.company)?.get(siteGroup.site)
                                ?? siteGroup.targets.length} targets
                            </span>
                          </div>
                          <div className="manual-target-list">
                            {siteGroup.targets.map((target) => (
                              <Link
                                key={target.target_id}
                                className="manual-target-card"
                                to={manualOperatingTargetPath(target.target_id)}
                              >
                                <div className="manual-target-name">{target.display_name}</div>
                                <div className="manual-target-key">{target.logical_node_key}</div>
                                <div className="manual-badges">
                                  <span className={`manual-badge ${target.ready ? 'online' : 'warning'}`}>
                                    {target.ready ? '준비 완료' : '설정 확인 필요'}
                                  </span>
                                  <span className={`manual-badge ${target.edge_connected ? 'online' : 'offline'}`}>
                                    {target.edge_connected ? 'edge 연결됨' : 'edge offline'}
                                  </span>
                                  <span className="manual-badge">camera {target.cameras.length}</span>
                                </div>
                                {!target.ready && (
                                  <div className="manual-subtitle">{target.not_ready_reasons.join(' · ')}</div>
                                )}
                              </Link>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  </section>
                )
              })}
              <Pagination
                page={effectivePage}
                pageSize={PAGE_SIZE}
                total={filteredTargets.length}
                itemLabel="targets"
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
